import React, { useContext, useEffect, useState } from 'react'
import { apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import OButton from 'components/reusable/OButton'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import dayjs from 'dayjs'
import Pagination from '../Pagination'
import AuthContext from 'context/AuthContext'
import { useTranslation } from 'react-i18next'
import FinalEmployeeTable from './AllEmployeeTable'
import EmployeeView from './EmployeeView'
import helpers from '../../utils/helpers'
import OSearch from 'components/reusable/OSearch'
import CustomSelect from 'components/reusable/CustomSelect'
import { leaveStatusOptions, employmentStatusOptions } from 'utils/constants' // ═══ ADDED employmentStatusOptions ═══
import { useLocation } from 'react-router-dom'
import EmployeeWageSetup from './EmployeeWageSetup'
import EmployeeIpModal from './EmployeeIpModal'

const AllEmployeeList = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10
  })
  const { updatePageName } = useContext(AuthContext)
  const [employeeList, setEmployeeList] = useState([])
  const [currentItem, setCurrentItem] = useState('')
  const [employeeView, setEmployeeView] = useState(false)
  const [wageSetupView, setWageSetupView] = useState(false)
  const [ipModalView, setIpModalView] = useState(false)
  const [filterData, setFilterData] = useState({
    startDate: '',
    endDate: '',
    leaveStatus: '',
    employmentStatus: '', // ═══ ADDED ═══
    searchKey: '',
    isFilter: false,
    isReset: false
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [sort, setSort] = useState({
    sortBy: 'acceptedRejectedAt',
    sortType: 'desc',
    sortColumn: 'hire_date'
  })
  
  // Define sorting options
  const [sortOptions] = useState([
    { value: 'a_z', label: 'A-Z' },
    { value: 'z_a', label: 'Z-A' }
  ])
  
  // Define column options for sorting
  const [columnOptions] = useState([
    { value: 'name', label: t('NAME') },
    { value: 'department', label: t('DEPARTMENT') },
    { value: 'job_title', label: t('JOB_TITLE') },
  ])

  const handleView = item => {
    setCurrentItem(item)
    setEmployeeView(!employeeView)
  }

  const handleWageSetup = item => {
    setCurrentItem(item)
    setWageSetupView(true)
  }

  const handleGeoIp = item => {
    setCurrentItem(item)
    setIpModalView(true)
  }

  const handlePageClick = event => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  const allEmployeeList = async (data) => {
    try {
      const { searchKey, isFilter, leaveStatus, employmentStatus, startDate, endDate } = filterData // ═══ ADDED employmentStatus ═══
      if (
        (data?.deletePage && employeeList?.length >= 1) ||
        (isFilter &&
          leaveStatus &&
          data?.statusChange &&
          employeeList?.length >= 1)
      ) {
        setPage(page - 1)
        setPaginationObj({ ...paginationObj, page: page - 1 })
      }

      // Map UI sort column to backend sortBy field
      const getSortByField = () => {
        switch (sort.sortColumn) {
          case 'name':
            return 'Employee.name'
          case 'department':
            return 'Department.name'
          case 'job_title':
            return 'Job.name'
          case 'hire_date':
            return 'acceptedRejectedAt'
          default:
            return 'createdAt'
        }
      }

      const payload = {
        leaveStatus: leaveStatus,
        employmentStatus: employmentStatus, // ═══ ADDED ═══
        page,
        pageSize,
        startDate: helpers?.ternaryCondition(
          startDate,
          dayjs(startDate).format('YYYY-MM-DD'),
          null
        ),
        endDate: helpers?.ternaryCondition(
          endDate,
          dayjs(endDate).format('YYYY-MM-DD'),
          null
        ),
        keyword: searchKey?.trim(),
        sortBy: getSortByField(),
        sortType: sort.sortType
      }
      const path = apiPath.allEmployeeList
      const result = await apiGet(path, payload)
      const response = result?.data?.results
      const resultStatus = result?.data?.success
      setEmployeeList(response?.docs)
      setPaginationObj({
        ...paginationObj,
        page: helpers?.ternaryCondition(resultStatus, response.page, null),
        pageCount: helpers?.ternaryCondition(
          resultStatus,
          response.totalPages,
          null
        ),
        perPageItem: helpers?.ternaryCondition(
          resultStatus,
          response?.docs.length,
          null
        ),
        totalItems: helpers?.ternaryCondition(
          resultStatus,
          response.totalDocs,
          null
        )
      })
    } catch (error) {
      console.log('error in get all employee list==>>>>', error.message)
    }
  }

  useEffect(() => {
    allEmployeeList()
  }, [page, filterData, sort])

  const handleReset = () => {
    setFilterData({
      searchKey: '',
      startDate: '',
      leaveStatus: '',
      employmentStatus: '', // ═══ ADDED ═══
      endDate: '',
      isReset: true,
      isFilter: false
    })
    setSort({
      sortBy: 'createdAt',
      sortType: 'desc',
      sortColumn: 'hire_date'
    })
    setPage(1)
    setSearchTerm('')
  }

  useEffect(() => {
    helpers.setDebouncingFunction({
      isInitialized,
      setIsInitialized,
      debouncedSearchTerm,
      searchTerm,
      filterData,
      setFilterData,
      setPage
    })
  }, [debouncedSearchTerm])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchTerm])

  useEffect(() => {
    updatePageName(t('NAV_ALL_EMPLOYEE_LIST'))
  }, [])

  const handleDateChange = (start, end) => {
    setPage(1)
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true
    })
  }

  const handleLeaveStatusChange = e => {
    setFilterData({ ...filterData, leaveStatus: e.target.value, isFilter: true })
    setPage(1)
  }

  // ═══ ADDED: Handle Employment Status Change ═══
  const handleEmploymentStatusChange = e => {
    setFilterData({ ...filterData, employmentStatus: e.target.value, isFilter: true })
    setPage(1)
  }

  // Handle sort order change (A-Z/Z-A)
  const handleSortOrderChange = e => {
    const orderValue = e.target.value
    setSort(prev => ({
      ...prev,
      sortType: orderValue === 'a_z' ? 'asc' : 'desc'
    }))
    setPage(1)
  }

  // Handle sort column change
  const handleSortColumnChange = e => {
    const columnValue = e.target.value
    setSort(prev => ({
      ...prev,
      sortColumn: columnValue,
      // Reset to ascending when changing column
      sortType: 'asc'
    }))
    setPage(1)
  }

  return (
    <>
      <div className='bg-[#F9F9F9]'>
        <div className='py-4 px-3'>
          <div className='bg-white border rounded-lg border-[#E9EDF9]'>
            <form className='border-b border-b-[#E3E3E3] gap-2 2xl:flex px-4 py-3'>
              <div className='col-span-2 flex flex-wrap items-center mb-2 2xl:mb-0'>
                <div className='flex items-center flex-wrap lg:pt-0 pt-3'>
                  <div className='relative flex mb-3 items-center'>
                    <OSearch
                      placeholder={t('SEARCH_BY_EMPLOYEE_NAME')}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  </div>
                  <ODateRangePicker
                    handleDateChange={handleDateChange}
                    setIsReset={setFilterData}
                    isReset={filterData?.isReset}
                  />
                  <CustomSelect
                    id='leaveStatus'
                    name='leaveStatus'
                    options={leaveStatusOptions}
                    placeholder='O_ALL'
                    value={filterData.leaveStatus}
                    onChange={handleLeaveStatusChange}
                    t={t}
                  />

                  {/* ═══ ADDED: Employment Status Dropdown ═══ */}
                  <CustomSelect
                    id='employmentStatus'
                    name='employmentStatus'
                    options={employmentStatusOptions}
                    placeholder='Employment Status'
                    value={filterData.employmentStatus}
                    onChange={handleEmploymentStatusChange}
                    t={t}
                    extraClasses='ml-3 mb-3'
                  />

                  {/* Sort Column Dropdown */}
                  <CustomSelect
                    id='sortColumn'
                    name='sortColumn'
                    options={columnOptions}
                    placeholder={t('SORT_BY_COLUMN')}
                    value={sort.sortColumn}
                    onChange={handleSortColumnChange}
                    t={t}
                    extraClasses='ml-3 mb-3'
                  />

                  {/* Sort Order Dropdown */}
                  <CustomSelect
                    id='sortOrder'
                    name='sortOrder'
                    options={sortOptions}
                    placeholder={t('SORT_ORDER')}
                    value={sort.sortType === 'asc' ? 'a_z' : 'z_a'}
                    onChange={handleSortOrderChange}
                    t={t}
                    extraClasses='ml-3 mb-3'
                  />

                  <OButton
                    label='Reset'
                    extraClasses='ml-3 mb-3'
                    style={{ borderRadius: '0.5rem' }}
                    type='button'
                    onClick={handleReset}
                  />
                </div>
              </div>
            </form>
            
            {employeeView && (
              <EmployeeView
                employeeView={employeeView}
                setEmployeeView={setEmployeeView}
                currentItem={currentItem}
                handleView={handleView}
              />
            )}
            
            {wageSetupView && (
              <EmployeeWageSetup
                employeeId={currentItem?.Employee?._id || currentItem?.employeeID}
                onClose={() => setWageSetupView(false)}
              />
            )}

            {ipModalView && (
              <EmployeeIpModal
                item={currentItem}
                onClose={() => setIpModalView(false)}
              />
            )}
            
            <FinalEmployeeTable
              handleView={handleView}
              handleWageSetup={handleWageSetup}
              handleGeoIp={handleGeoIp}
              currentItem={currentItem}
              employeeView={employeeView}
              setSort={setSort}
              sort={sort}
              employeeList={employeeList}
              allEmployeeList={allEmployeeList}
              page={page}
            />
            {paginationObj?.totalItems ? (
              <Pagination
                page={page}
                handlePageClick={handlePageClick}
                options={paginationObj}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

export default AllEmployeeList